<template>
  <div v-if="showTaxesSection" class="modal-inline-taxes">
    <header class="modal-inline-taxes__head expense-modal-box__head">
      <div class="modal-taxes-head-copy">
        <h4>{{ resolvedText.title }}</h4>
        <p>{{ resolvedText.subtitle }}</p>
      </div>

      <label class="app-modal-toggle modal-taxes-toggle" :class="{ 'app-modal-toggle--disabled': disabled }">
        <input
          type="checkbox"
          :checked="withoutTaxesEnabled"
          :disabled="disabled"
          @change="$emit('toggle-without-taxes', $event)"
        />
        <span>{{ resolvedText.withoutTaxes }}</span>
      </label>
    </header>

    <div v-if="showTaxSelectors" class="app-modal__grid two-columns">
      <div class="app-modal-field">
        <span>{{ resolvedText.taxType }}</span>
        <div class="modal-tax-mode-row">
          <label
            v-for="option in taxModeCheckboxOptions"
            :key="option.value"
            :class="[
              'app-modal-toggle',
              'modal-tax-mode-toggle',
              { 'app-modal-toggle--disabled': disabled }
            ]"
          >
            <input
              type="checkbox"
              :checked="isTaxModeSelected(option.value)"
              :disabled="disabled"
              @change="selectTaxMode(option.value)"
            />
            <span>{{ option.label }}</span>
          </label>
        </div>
      </div>

      <div class="app-modal-field">
        <span>{{ resolvedText.taxes }}</span>
        <multiselect
          v-if="form.taxType === 'individual'"
          v-model="taxModel"
          :options="options.taxes"
          track-by="value"
          label="label"
          :show-labels="false"
          :searchable="true"
          :allow-empty="true"
          :disabled="disabled"
          class="app-modal-select"
          :placeholder="resolvedText.placeholders.taxes"
          @input="taxModel = $event"
        >
          <template #noResult><span>{{ resolvedCommonText.noOption }}</span></template>
          <template #noOptions><span>{{ resolvedCommonText.noOption }}</span></template>
        </multiselect>

        <multiselect
          v-else
          v-model="form.taxGroup"
          :options="options.taxGroups"
          track-by="value"
          label="label"
          :show-labels="false"
          :searchable="true"
          :allow-empty="true"
          :disabled="disabled"
          class="app-modal-select"
          :placeholder="resolvedText.placeholders.group"
          @input="form.taxGroup = $event"
        >
          <template #noResult><span>{{ resolvedCommonText.noOption }}</span></template>
          <template #noOptions><span>{{ resolvedCommonText.noOption }}</span></template>
        </multiselect>
      </div>
    </div>
  </div>
</template>

<script>
const defaultText = {
  title: 'Impuestos',
  subtitle: 'Selecciona el tipo de impuestos aplicables para este registro.',
  withoutTaxes: 'Sin impuestos',
  taxType: 'Tipo de impuestos',
  taxes: 'Impuestos',
  placeholders: {
    taxes: 'Selecciona impuestos',
    group: 'Selecciona grupo',
  },
  fallbackTaxLabel: ({ id }) => `Impuesto #${id}`,
  fallbackGroupLabel: 'Grupo',
  fallbackIndividualLabel: 'Individual',
};

const defaultCommonText = {
  noOption: 'Sin opcion disponible',
};

export default {
  name: 'AppGeneralTaxesBlock',
  props: {
    showTaxesSection: {
      type: Boolean,
      default: false,
    },
    showTaxSelectors: {
      type: Boolean,
      default: false,
    },
    withoutTaxesEnabled: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    form: {
      type: Object,
      required: true,
    },
    options: {
      type: Object,
      required: true,
    },
    text: {
      type: Object,
      default: () => ({}),
    },
    commonText: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ['toggle-without-taxes'],
  created() {
    this.ensureTaxTypeDefault();
  },
  computed: {
    resolvedText() {
      const source = this.text && typeof this.text === 'object' ? this.text : {};
      return {
        ...defaultText,
        ...source,
        placeholders: {
          ...defaultText.placeholders,
          ...(source.placeholders && typeof source.placeholders === 'object' ? source.placeholders : {}),
        },
      };
    },
    resolvedCommonText() {
      const source = this.commonText && typeof this.commonText === 'object' ? this.commonText : {};
      return {
        ...defaultCommonText,
        ...source,
      };
    },
    taxModeCheckboxOptions() {
      const list = Array.isArray(this.options?.taxModes) ? this.options.taxModes : [];

      const individual = list
        .find((option) => String(option?.value || '').trim().toLowerCase() === 'individual');
      const group = list
        .find((option) => String(option?.value || '').trim().toLowerCase() === 'group');

      return [
        {
          value: 'individual',
          label: String(individual?.label || this.resolvedText.fallbackIndividualLabel),
        },
        {
          value: 'group',
          label: String(group?.label || this.resolvedText.fallbackGroupLabel),
        },
      ];
    },
    taxModel: {
      get() {
        const list = Array.isArray(this.form?.taxes) ? this.form.taxes : [];
        return this.toTaxOption(list[0]);
      },
      set(option) {
        this.form.taxes = option ? [option] : [];
      },
    },
  },
  methods: {
    ensureTaxTypeDefault() {
      const mode = String(this.form?.taxType || '').trim().toLowerCase();
      if (mode !== 'individual' && mode !== 'group') {
        this.form.taxType = 'individual';
      }
    },
    isTaxModeSelected(value) {
      const current = String(this.form?.taxType || 'individual').trim().toLowerCase();
      return current === String(value || '').trim().toLowerCase();
    },
    selectTaxMode(value) {
      if (this.disabled) {
        return;
      }

      const normalized = String(value || 'individual').trim().toLowerCase();
      this.form.taxType = normalized === 'group' ? 'group' : 'individual';
    },
    toTaxOption(rawValue) {
      if (rawValue && typeof rawValue === 'object') {
        return rawValue;
      }

      const numericId = Number(rawValue);
      if (!Number.isFinite(numericId) || numericId <= 0) {
        return null;
      }

      const fromCatalog = (Array.isArray(this.options?.taxes) ? this.options.taxes : [])
        .find((option) => Number(option?.value || option?.id || 0) === numericId);

      return fromCatalog || { value: numericId, label: this.resolvedText.fallbackTaxLabel({ id: numericId }) };
    },
  },
};
</script>

<style scoped>
.modal-taxes-head-copy {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.modal-taxes-head-copy p {
  margin: 4px 0 0;
  font-size: 12px;
  color: #6b7280;
}

.modal-taxes-toggle {
  margin: 0;
  width: 25%;
}

.modal-inline-taxes {
  margin-top: 10px;
}

.modal-inline-taxes__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
}

.modal-tax-mode-row {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.modal-tax-mode-toggle {
  min-height: 38px;
  border: 1px solid rgba(153, 152, 153, 0.3);
  border-radius: 8px;
  background: #ffffff;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0 10px;
  margin: 0;
}

@media (max-width: 900px) {
  .modal-inline-taxes__head {
    flex-direction: column;
    align-items: stretch;
  }

  .modal-tax-mode-row {
    grid-template-columns: 1fr;
  }

  .modal-taxes-toggle {
    width: 100%;
  }
}
</style>
